import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { SEC } from './SecurityTheme';
import AlertFeed from './AlertFeed';
import CollectionStatus from './CollectionStatus';
import {
  getAllSecurityAlerts,
  getClients,
  SecurityAlertsResponse,
} from '../../services/api';
import { format } from 'date-fns';
import {
  Shield,
  AlertTriangle,
  Activity,
  MonitorSpeaker,
  Radio,
  ChevronRight,
  Globe,
  Zap,
  Users,
  Lock,
} from 'lucide-react';

// Mock sign-in activity data for the chart (will be replaced with real data)
const MOCK_SIGNIN_DATA = Array.from({ length: 24 }, (_, i) => ({
  hour: `${String(i).padStart(2, '0')}:00`,
  normal: Math.floor(Math.random() * 80) + 20,
  suspicious: Math.floor(Math.random() * 10),
  blocked: Math.floor(Math.random() * 5),
}));

// IOC type labels for display
const IOC_LABELS: Record<string, string> = {
  suspicious_inbox_rules: 'Suspicious Inbox Rules',
  foreign_signin: 'Foreign Sign-Ins',
  legacy_auth: 'Legacy Authentication',
  risky_signin: 'Risky Sign-Ins',
  external_forwarding: 'External Forwarding',
  no_mfa: 'No MFA Configured',
  password_resets: 'Password Resets',
  known_attacker_ip: 'Known Attacker IPs',
  attacker_email: 'Attacker-Sent Emails',
  oauth_consent: 'OAuth App Consent',
  mass_rule_cleanup: 'Mass Rule Cleanup',
  cross_user_ip: 'Cross-User IP Correlation',
};

export default function SecurityDashboard() {
  const [alertsData, setAlertsData] = useState<SecurityAlertsResponse | null>(null);
  const [clientCount, setClientCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [now, setNow] = useState(new Date());

  const fetchData = useCallback(async () => {
    try {
      const [alertsResp, clientsResp] = await Promise.all([
        getAllSecurityAlerts({ limit: 100 }),
        getClients({ page: 1, pageSize: 1 }),
      ]);
      if (alertsResp.success) setAlertsData(alertsResp);
      if (clientsResp.total !== undefined) setClientCount(clientsResp.total);
    } catch (err) {
      console.error('Failed to load security dashboard data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const timer = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(timer);
  }, [fetchData]);

  // Compute threat level
  const criticalCount = alertsData?.bySeverity?.critical || 0;
  const warningCount = alertsData?.bySeverity?.warning || 0;
  const openAlerts = (alertsData?.byStatus?.new || 0) + (alertsData?.byStatus?.acknowledged || 0) + (alertsData?.byStatus?.investigating || 0);
  const threatLevel = criticalCount > 5 ? 'CRITICAL' : criticalCount > 0 ? 'ELEVATED' : warningCount > 3 ? 'ADVISORY' : 'NOMINAL';
  const threatColor = threatLevel === 'CRITICAL' ? SEC.red : threatLevel === 'ELEVATED' ? SEC.amber : threatLevel === 'ADVISORY' ? SEC.amber : SEC.green;

  // Compute IOC breakdown from alert data
  const iocBreakdown: Record<string, number> = {};
  if (alertsData?.alerts) {
    for (const alert of alertsData.alerts) {
      for (const ioc of alert.iocTypes) {
        iocBreakdown[ioc] = (iocBreakdown[ioc] || 0) + 1;
      }
    }
  }
  const iocEntries = Object.entries(iocBreakdown).sort((a, b) => b[1] - a[1]).slice(0, 8);
  const maxIocCount = iocEntries.length > 0 ? iocEntries[0][1] : 1;

  // Stat cards config
  const stats = [
    {
      label: 'TENANTS MONITORED',
      value: clientCount,
      icon: MonitorSpeaker,
      color: SEC.cyan,
      sub: 'Active GDAP connections',
    },
    {
      label: 'OPEN ALERTS',
      value: openAlerts,
      icon: AlertTriangle,
      color: openAlerts > 0 ? SEC.red : SEC.green,
      sub: `${criticalCount} critical / ${warningCount} warning`,
    },
    {
      label: 'ACTIVE QUARANTINES',
      value: alertsData?.alerts?.filter(a => a.autoQuarantineApplied).length || 0,
      icon: Lock,
      color: SEC.amber,
      sub: 'Auto-quarantine actions',
    },
    {
      label: 'THREAT LEVEL',
      value: threatLevel,
      icon: Zap,
      color: threatColor,
      sub: 'Current posture assessment',
      isText: true,
    },
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: SEC.bg }}>
        <div className="text-center">
          <div
            className="animate-spin rounded-full h-10 w-10 border-2 mx-auto mb-4"
            style={{ borderColor: SEC.border, borderTopColor: SEC.cyan }}
          />
          <p className="text-sm font-mono tracking-wider" style={{ color: SEC.t3 }}>
            INITIALIZING SOC...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: SEC.bg }}>
      {/* Header */}
      <div
        className="sticky top-0 z-10 px-6 py-3 flex items-center justify-between"
        style={{
          background: SEC.header,
          borderBottom: `1px solid ${SEC.border}`,
          boxShadow: `0 1px 8px rgba(0,0,0,0.4)`,
        }}
      >
        <div className="flex items-center gap-3">
          <Shield className="h-5 w-5" style={{ color: SEC.cyan }} />
          <h1
            className="text-sm font-bold tracking-[0.2em] uppercase"
            style={{ color: SEC.t1 }}
          >
            Security Operations Center
          </h1>
          <span
            className="flex items-center gap-1.5 text-[10px] font-bold tracking-wider px-2 py-0.5 rounded-full"
            style={{
              color: SEC.green,
              background: `${SEC.green}15`,
              border: `1px solid ${SEC.green}40`,
            }}
          >
            <span
              className="inline-block w-1.5 h-1.5 rounded-full animate-pulse"
              style={{ background: SEC.green }}
            />
            LIVE
          </span>
        </div>
        <div className="flex items-center gap-4">
          <Link
            to="/security/alerts"
            className="text-xs font-medium px-3 py-1.5 rounded transition-colors flex items-center gap-1"
            style={{
              color: SEC.cyan,
              border: `1px solid ${SEC.cyanDim}`,
              background: SEC.cyanGlow,
            }}
          >
            Alerts Console
            <ChevronRight className="h-3 w-3" />
          </Link>
          <Link
            to="/security/map"
            className="text-xs font-medium px-3 py-1.5 rounded transition-colors flex items-center gap-1"
            style={{
              color: SEC.t2,
              border: `1px solid ${SEC.border}`,
            }}
          >
            <Globe className="h-3 w-3" />
            Threat Map
          </Link>
          <span className="font-mono text-xs" style={{ color: SEC.t3 }}>
            {format(now, 'yyyy-MM-dd HH:mm:ss')} UTC
          </span>
        </div>
      </div>

      <div className="p-6 space-y-6">
        {/* Stats Row */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {stats.map((stat) => {
            const Icon = stat.icon;
            return (
              <div
                key={stat.label}
                className="rounded-lg p-4 relative overflow-hidden"
                style={{
                  background: SEC.panel,
                  border: `1px solid ${SEC.border}`,
                }}
              >
                {/* Glow accent */}
                <div
                  className="absolute top-0 left-0 w-full h-0.5"
                  style={{ background: `linear-gradient(90deg, ${stat.color}60, transparent)` }}
                />
                <div className="flex items-start justify-between">
                  <div>
                    <p
                      className="text-[10px] font-bold tracking-[0.15em] uppercase mb-1"
                      style={{ color: SEC.t3 }}
                    >
                      {stat.label}
                    </p>
                    <p
                      className={`font-bold ${stat.isText ? 'text-lg' : 'text-2xl'} font-mono`}
                      style={{ color: stat.color }}
                    >
                      {stat.value}
                    </p>
                    <p className="text-[10px] mt-1" style={{ color: SEC.t4 }}>
                      {stat.sub}
                    </p>
                  </div>
                  <div
                    className="p-2 rounded-lg"
                    style={{ background: `${stat.color}10` }}
                  >
                    <Icon className="h-5 w-5" style={{ color: stat.color }} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Sign-in Activity Chart (left 2 cols) */}
          <div
            className="lg:col-span-2 rounded-lg overflow-hidden"
            style={{ background: SEC.panel, border: `1px solid ${SEC.border}` }}
          >
            <div
              className="flex items-center justify-between px-4 py-3"
              style={{ borderBottom: `1px solid ${SEC.border}` }}
            >
              <div className="flex items-center gap-2">
                <Activity className="h-4 w-4" style={{ color: SEC.cyan }} />
                <h3
                  className="text-xs font-semibold tracking-[0.15em] uppercase"
                  style={{ color: SEC.t1 }}
                >
                  Sign-In Activity (24h)
                </h3>
              </div>
              <div className="flex items-center gap-4 text-[10px]">
                <span className="flex items-center gap-1">
                  <span className="w-2 h-2 rounded-sm" style={{ background: SEC.cyan }} />
                  <span style={{ color: SEC.t3 }}>Normal</span>
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-2 h-2 rounded-sm" style={{ background: SEC.amber }} />
                  <span style={{ color: SEC.t3 }}>Suspicious</span>
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-2 h-2 rounded-sm" style={{ background: SEC.red }} />
                  <span style={{ color: SEC.t3 }}>Blocked</span>
                </span>
              </div>
            </div>
            {/* Simple CSS-based bar chart since Recharts isn't available */}
            <div className="px-4 py-4">
              <div className="flex items-end gap-1 h-40">
                {MOCK_SIGNIN_DATA.map((d, i) => {
                  const maxVal = 100;
                  const normalH = (d.normal / maxVal) * 100;
                  const suspH = (d.suspicious / maxVal) * 100;
                  const blockedH = (d.blocked / maxVal) * 100;
                  return (
                    <div key={i} className="flex-1 flex flex-col items-center gap-0.5 relative group">
                      <div className="w-full flex flex-col justify-end" style={{ height: '140px' }}>
                        <div
                          className="w-full rounded-t-sm transition-all"
                          style={{ height: `${blockedH}%`, background: SEC.red, minHeight: d.blocked > 0 ? '2px' : 0 }}
                        />
                        <div
                          className="w-full transition-all"
                          style={{ height: `${suspH}%`, background: SEC.amber, minHeight: d.suspicious > 0 ? '2px' : 0 }}
                        />
                        <div
                          className="w-full rounded-b-sm transition-all"
                          style={{ height: `${normalH}%`, background: `${SEC.cyan}80` }}
                        />
                      </div>
                      {i % 3 === 0 && (
                        <span className="text-[8px] font-mono" style={{ color: SEC.t4 }}>
                          {d.hour.slice(0, 2)}
                        </span>
                      )}
                      {/* Tooltip on hover */}
                      <div
                        className="absolute bottom-full mb-2 hidden group-hover:block z-10 px-2 py-1 rounded text-[10px] whitespace-nowrap pointer-events-none"
                        style={{ background: SEC.panel2, border: `1px solid ${SEC.border}`, color: SEC.t1 }}
                      >
                        {d.hour}: {d.normal} normal, {d.suspicious} suspicious, {d.blocked} blocked
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="flex justify-between mt-1">
                <span className="text-[9px] font-mono" style={{ color: SEC.t4 }}>00:00</span>
                <span className="text-[9px] font-mono" style={{ color: SEC.t4 }}>12:00</span>
                <span className="text-[9px] font-mono" style={{ color: SEC.t4 }}>23:00</span>
              </div>
            </div>
          </div>

          {/* IOC Breakdown (right col) */}
          <div
            className="rounded-lg overflow-hidden"
            style={{ background: SEC.panel, border: `1px solid ${SEC.border}` }}
          >
            <div
              className="flex items-center justify-between px-4 py-3"
              style={{ borderBottom: `1px solid ${SEC.border}` }}
            >
              <div className="flex items-center gap-2">
                <Radio className="h-4 w-4" style={{ color: SEC.amber }} />
                <h3
                  className="text-xs font-semibold tracking-[0.15em] uppercase"
                  style={{ color: SEC.t1 }}
                >
                  IOC Breakdown
                </h3>
              </div>
              <span className="text-[10px] font-mono" style={{ color: SEC.t3 }}>
                {iocEntries.reduce((sum, [, v]) => sum + v, 0)} total
              </span>
            </div>
            <div className="px-4 py-3 space-y-2.5">
              {iocEntries.length === 0 ? (
                <p className="text-center py-4 text-xs" style={{ color: SEC.t3 }}>
                  No IOC detections yet
                </p>
              ) : (
                iocEntries.map(([type, count]) => {
                  const pct = (count / maxIocCount) * 100;
                  return (
                    <div key={type}>
                      <div className="flex items-center justify-between mb-0.5">
                        <span className="text-[10px] font-medium truncate" style={{ color: SEC.t2 }}>
                          {IOC_LABELS[type] || type}
                        </span>
                        <span className="text-[10px] font-mono ml-2" style={{ color: SEC.t1 }}>
                          {count}
                        </span>
                      </div>
                      <div className="w-full h-1.5 rounded-full overflow-hidden" style={{ background: SEC.border }}>
                        <div
                          className="h-full rounded-full transition-all duration-700"
                          style={{
                            width: `${pct}%`,
                            background: count === maxIocCount ? SEC.red : SEC.cyan,
                          }}
                        />
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>

        {/* Bottom Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Live Alert Feed */}
          <div
            className="rounded-lg overflow-hidden"
            style={{ background: SEC.panel, border: `1px solid ${SEC.border}` }}
          >
            <div
              className="flex items-center justify-between px-4 py-3"
              style={{ borderBottom: `1px solid ${SEC.border}` }}
            >
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-4 w-4" style={{ color: SEC.red }} />
                <h3
                  className="text-xs font-semibold tracking-[0.15em] uppercase"
                  style={{ color: SEC.t1 }}
                >
                  Live Alert Feed
                </h3>
                {openAlerts > 0 && (
                  <span
                    className="text-[10px] font-bold px-1.5 py-0.5 rounded-full"
                    style={{ color: SEC.red, background: `${SEC.red}20`, border: `1px solid ${SEC.red}40` }}
                  >
                    {openAlerts}
                  </span>
                )}
              </div>
              <Link
                to="/security/alerts"
                className="text-[10px] font-medium flex items-center gap-1"
                style={{ color: SEC.cyan }}
              >
                View All
                <ChevronRight className="h-3 w-3" />
              </Link>
            </div>
            <AlertFeed compact maxItems={15} autoRefreshMs={30000} />
          </div>

          {/* Client Security Status */}
          <div
            className="rounded-lg overflow-hidden"
            style={{ background: SEC.panel, border: `1px solid ${SEC.border}` }}
          >
            <div
              className="flex items-center justify-between px-4 py-3"
              style={{ borderBottom: `1px solid ${SEC.border}` }}
            >
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4" style={{ color: SEC.cyan }} />
                <h3
                  className="text-xs font-semibold tracking-[0.15em] uppercase"
                  style={{ color: SEC.t1 }}
                >
                  Client Security Status
                </h3>
              </div>
            </div>
            <ClientSecurityTable alerts={alertsData?.alerts || []} />
          </div>
        </div>

        {/* Collection Engine Status */}
        <CollectionStatus compact />
      </div>
    </div>
  );
}

// Sub-component: client security status table
function ClientSecurityTable({ alerts }: { alerts: { clientId: string; clientName: string; severity: string; status: string }[] }) {
  // Aggregate alerts per client
  const clientMap = new Map<string, { name: string; critical: number; warning: number; info: number; open: number }>();
  for (const a of alerts) {
    if (!clientMap.has(a.clientId)) {
      clientMap.set(a.clientId, { name: a.clientName, critical: 0, warning: 0, info: 0, open: 0 });
    }
    const entry = clientMap.get(a.clientId)!;
    if (a.severity === 'critical') entry.critical++;
    else if (a.severity === 'warning') entry.warning++;
    else entry.info++;
    if (a.status === 'new' || a.status === 'acknowledged' || a.status === 'investigating') entry.open++;
  }

  const clients = Array.from(clientMap.entries())
    .map(([id, data]) => ({ id, ...data }))
    .sort((a, b) => b.critical - a.critical || b.warning - a.warning);

  if (clients.length === 0) {
    return (
      <div className="text-center py-8" style={{ color: SEC.t3 }}>
        <p className="text-xs">No client alert data available</p>
        <p className="text-[10px] mt-1">Run a collection to populate this table</p>
      </div>
    );
  }

  return (
    <div className="overflow-y-auto" style={{ maxHeight: '320px' }}>
      <table className="w-full text-xs">
        <thead>
          <tr style={{ borderBottom: `1px solid ${SEC.border}` }}>
            <th className="text-left px-4 py-2 font-medium uppercase tracking-wider" style={{ color: SEC.t3, fontSize: '10px' }}>Client</th>
            <th className="text-center px-2 py-2 font-medium uppercase tracking-wider" style={{ color: SEC.t3, fontSize: '10px' }}>Critical</th>
            <th className="text-center px-2 py-2 font-medium uppercase tracking-wider" style={{ color: SEC.t3, fontSize: '10px' }}>Warning</th>
            <th className="text-center px-2 py-2 font-medium uppercase tracking-wider" style={{ color: SEC.t3, fontSize: '10px' }}>Info</th>
            <th className="text-center px-2 py-2 font-medium uppercase tracking-wider" style={{ color: SEC.t3, fontSize: '10px' }}>Open</th>
            <th className="text-right px-4 py-2 font-medium uppercase tracking-wider" style={{ color: SEC.t3, fontSize: '10px' }}>Status</th>
          </tr>
        </thead>
        <tbody>
          {clients.map((c) => {
            const statusLabel = c.critical > 0 ? 'AT RISK' : c.warning > 0 ? 'ADVISORY' : 'OK';
            const statusClr = c.critical > 0 ? SEC.red : c.warning > 0 ? SEC.amber : SEC.green;
            return (
              <tr
                key={c.id}
                className="transition-colors hover:bg-white/[0.02]"
                style={{ borderBottom: `1px solid ${SEC.border}` }}
              >
                <td className="px-4 py-2">
                  <Link
                    to={`/clients/${c.id}`}
                    className="font-medium hover:underline"
                    style={{ color: SEC.t1 }}
                  >
                    {c.name}
                  </Link>
                </td>
                <td className="text-center px-2 py-2">
                  {c.critical > 0 ? (
                    <span className="font-mono font-bold" style={{ color: SEC.red }}>{c.critical}</span>
                  ) : (
                    <span style={{ color: SEC.t4 }}>0</span>
                  )}
                </td>
                <td className="text-center px-2 py-2">
                  {c.warning > 0 ? (
                    <span className="font-mono font-bold" style={{ color: SEC.amber }}>{c.warning}</span>
                  ) : (
                    <span style={{ color: SEC.t4 }}>0</span>
                  )}
                </td>
                <td className="text-center px-2 py-2">
                  {c.info > 0 ? (
                    <span className="font-mono" style={{ color: SEC.cyan }}>{c.info}</span>
                  ) : (
                    <span style={{ color: SEC.t4 }}>0</span>
                  )}
                </td>
                <td className="text-center px-2 py-2">
                  {c.open > 0 ? (
                    <span className="font-mono font-bold" style={{ color: SEC.red }}>{c.open}</span>
                  ) : (
                    <span style={{ color: SEC.t4 }}>0</span>
                  )}
                </td>
                <td className="text-right px-4 py-2">
                  <span
                    className="text-[10px] font-bold tracking-wider px-2 py-0.5 rounded-full"
                    style={{
                      color: statusClr,
                      background: `${statusClr}15`,
                      border: `1px solid ${statusClr}30`,
                    }}
                  >
                    {statusLabel}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
